import { useEffect, useMemo, useState } from "react";
import Table from "@/components/Table/Table";
import Pagination from "@/components/Table/Pagination";
import type { Column } from "@/types/common";
import auditLogService from "@/services/api/auditLog";
import type { AuditLogEntry } from "@/services/api/auditLog";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getActionLogs } from "@/store/slices/auditLogSlice";
import ExportConfirmationModal from "@/components/ExportConfirmationModal/ExportConfirmationModal";
import Button from "@/components/Button/Button";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import ChevronDownIcon from "@/components/SVGComponents/ChevronDownIcon";
import { combineClasses } from "@/lib/utils";
import { toOffsetDateTime } from "@/lib/dateTime";
import AuditCard from "./components/AuditCard";
import { formatAuditDate, formatAuditTime, getAuditSource } from "./lib/auditFormat";
import styles from "./AuditLog.module.scss";
import { TableCellTooltip } from "@/components/Tooltip";

const MODULE_OPTIONS = [
  { label: "Event", value: "Event" },
  { label: "Event location", value: "EventLocation" },
  { label: "Event category", value: "EventVisibleCategory" },
  { label: "Venue", value: "Venue" },
  { label: "Temp order", value: "TempOrder" },
  { label: "Chapter", value: "Chapter" },
  { label: "User", value: "User" },
  { label: "Question", value: "QUESTION" },
];

const DATE_RANGE_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
];

const startOfDayIso = (daysAgo: number) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - daysAgo);
  return toOffsetDateTime(date);
};

const resolveDateRange = (preset: string | null): { startDate?: string; endDate?: string } => {
  if (preset === "today") return { startDate: startOfDayIso(0) };
  if (preset === "7d") return { startDate: startOfDayIso(6) };
  if (preset === "30d") return { startDate: startOfDayIso(29) };
  return {};
};

const getActorLabel = (entry: AuditLogEntry) => {
  if (entry.actorName) return entry.actorName;
  if (entry.actorId != null) return `Actor #${entry.actorId}`;
  return "Unknown";
};

const AuditLog = () => {
  const dispatch = useAppDispatch();
  const { entries, totalEntries, isFetching } = useAppSelector((state) => state.auditLog);

  const [moduleFilter, setModuleFilter] = useState<string | null>(null);
  const [dateRangeFilter, setDateRangeFilter] = useState<string | null>(null);
  const [pageSelected, setPageSelected] = useState(1);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const rowsPerPageValue = 10;

  const formConfig = useMemo(
    () => ({
      module: {
        controlType: "select" as const,
        label: "Module",
        isControlled: true,
        props: { options: MODULE_OPTIONS },
      },
      dateRange: {
        controlType: "select" as const,
        label: "Date",
        isControlled: true,
        props: { options: DATE_RANGE_OPTIONS },
      },
    }),
    [],
  );

  const dateRange = resolveDateRange(dateRangeFilter);

  useEffect(() => {
    dispatch(
      getActionLogs({
        pageNo: pageSelected,
        perPage: rowsPerPageValue,
        module: moduleFilter || undefined,
        ...dateRange,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageSelected, moduleFilter, dateRangeFilter]);

  const columns: Column<AuditLogEntry>[] = [
    {
      name: "Time",
      width: "14%",
      cell: (row) => (
        <div className={styles.time}>
          <span className={styles.timeValue}>{formatAuditTime(row?.createdAt)}</span>
          <span className={styles.timeSubtitle}>{formatAuditDate(row?.createdAt)}</span>
        </div>
      ),
    },
    { name: "Admin", width: "20%", selector: (row) => getActorLabel(row) },
    {
      name: "Source",
      width: "14%",
      cell: (row) => {
        const source = getAuditSource(row);
        return <span className={source === "Member" ? styles.sourceMember : styles.sourceAdmin}>{source}</span>;
      },
    },
    {name: "Action", width: "34%", cell: (row) => <TableCellTooltip value={row?.action || "—"} copyable />},
    { name: "Module", width: "18%", selector: (row) => row?.module || "—" },
  ];

  return (
    <>
      {/* Desktop View: untouched existing implementation */}
      <div className={styles.desktopView}>
        <Table<AuditLogEntry>
          columns={columns}
          data={entries}
          progressPending={isFetching}
          header={{
            titleName: "Activity",
            showSearch: false,
            formConfig,
            onFilterSubmit: (data) => {
              setModuleFilter((data.module as string | undefined) ?? null);
              setDateRangeFilter((data.dateRange as string | undefined) ?? null);
              setPageSelected(1);
            },
            exportLabel: "EXPORT LOG",
            onExport: () => setIsExportModalOpen(true),
          }}
          paginationProps={{
            isPagination: true,
            pageSelected,
            totalCount: totalEntries,
            rowsPerPageValue,
            setPageSelected,
          }}
        />
      </div>

      {/* Mobile View: responsive card list & controls following Members screen pattern */}
      <div className={styles.mobileView}>

        {/* Mobile Controls: Filter Selects and Export Button */}
        <div className={styles.mobileControlsRow}>
          <div className={styles.mobileFiltersGroup}>
            <div className={styles.mobileFilterWrapper}>
              <select
                value={moduleFilter || ""}
                onChange={(e) => {
                  setModuleFilter(e.target.value || null);
                  setPageSelected(1);
                }}
                aria-label="Filter by module"
              >
                <option value="">Module: All</option>
                {MODULE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    Module: {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className={styles.selectChevron} />
            </div>

            <div className={styles.mobileFilterWrapper}>
              <select
                value={dateRangeFilter || ""}
                onChange={(e) => {
                  setDateRangeFilter(e.target.value || null);
                  setPageSelected(1);
                }}
                aria-label="Filter by date range"
              >
                <option value="">Date: All</option>
                {DATE_RANGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    Date: {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className={styles.selectChevron} />
            </div>
          </div>

          <Button
            outlined
            onClick={() => setIsExportModalOpen(true)}
            className={combineClasses(styles.mobileActionBtn, styles.mobileExportBtn)}
          >
            <DownloadIcon className={styles.actionBtnIcon} />
            <span>Export Logs</span>
          </Button>
        </div>

        {/* Mobile Activity Cards List */}
        <div className={styles.mobileCardList}>
          {isFetching ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className={styles.mobileSkeletonCard}>
                <div className={styles.skeletonHeader}>
                  <div className={styles.skeletonAvatar} />
                  <div className={styles.skeletonTextGroup}>
                    <div className={combineClasses(styles.skeletonLine, styles.short)} />
                    <div className={combineClasses(styles.skeletonLine, styles.long)} />
                  </div>
                </div>
                <div className={styles.skeletonGrid}>
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                </div>
              </div>
            ))
          ) : entries.length === 0 ? (
            <div className={styles.mobileEmptyState}>
              <h4 className={styles.mobileEmptyTitle}>No audit logs found</h4>
              <p className={styles.mobileEmptyCaption}>Try clearing your filters.</p>
            </div>
          ) : (
            entries.map((entry) => (
              <AuditCard key={entry.id} entry={entry} />
            ))
          )}
        </div>

        {/* Mobile Pagination */}
        {totalEntries > rowsPerPageValue && (
          <div className={styles.mobilePaginationContainer}>
            <Pagination
              pageSelected={pageSelected}
              totalCount={totalEntries}
              rowsPerPageValue={rowsPerPageValue}
              setPageSelected={setPageSelected}
            />
          </div>
        )}
      </div>

      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        fallbackFileName="audit-log"
        fetchExportFile={() => auditLogService.exportActionLogs({ module: moduleFilter || undefined, ...dateRange })}
      />
    </>
  );
};

export default AuditLog;

